import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useHistory, useLocation, useParams, useRouteMatch } from "react-router-dom";

import { useEntityIdContext } from "../contexts/entityIdContext";
import { tabs } from "../string";

interface IParams {
	repId: string | null;
	tab: string | null;
	entityId?: string | null;
}

export const useEntityId = () => {
	const { entityId } = useParams<IParams>();
	const { pathname } = useLocation();
	const tab = useSelectedTab();
	const ctx = useEntityIdContext();

	const entityIdFromUrl = useMemo(() => {
		const urlParts = pathname.split("/");
		const tabIndex = urlParts.indexOf(tab) + 1;
		if (tabIndex === 0 || tabIndex == urlParts.length) return undefined;
		return urlParts[tabIndex];
	}, [pathname, tab]);

	if (entityId) return parseInt(entityId);
	if (ctx.entityId) return ctx.entityId;
	if (entityIdFromUrl) return parseInt(entityIdFromUrl);
	return undefined;
};

export const useRepId = () => {
	const { repId } = useParams<IParams>();

	if (!repId) return undefined;

	return parseInt(repId);
};

export const useUpdateRep = () => {
	const history = useHistory();
	const queryClient = useQueryClient();

	return async (newRepId: number | null, tab?: string, entityId?: number) => {
		if (tab) {
			if (entityId) history.replace("/AgentPortal/" + newRepId + "/" + tab + "/" + entityId);
			else history.replace("/AgentPortal/" + newRepId + "/" + tab);
		} else {
			history.replace("/AgentPortal/" + newRepId);
		}

		await queryClient.invalidateQueries();

		return;
	};
};

export const useUpdateRoute = () => {
	const history = useHistory();
	const { url } = useRouteMatch();
	const selectedTab = useSelectedTab();
	const baseUrl = useMemo(() => (url.split("/").length >= 3 ? url.split("/").slice(0, 3).join("/") : "/"), [url]);

	return useCallback(
		(entityId?: number, tab?: string, params?: Record<string, string>) => {
			tab = tab ?? selectedTab;
			let newUrl = "";

			if (baseUrl === "/") newUrl = "/AgentPortal/null/" + [tab, entityId].filter((item) => item).join("/");
			else newUrl = [baseUrl, tab, entityId].filter((item) => item).join("/");

			if (params && Object.keys(params).length > 0) {
				const query = Object.entries(params)
					.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
					.join("&");
				newUrl += `?${query}`;
			}

			if (newUrl !== url) history.push(newUrl);
		},
		[selectedTab, baseUrl, history, url],
	);
};

export const useSelectedTab = () => {
	const { pathname } = useLocation();

	const selectedTab = useMemo(
		() =>
			pathname
				.split("/")
				.filter((item) => !Number(item) && Number(item) !== 0)
				.filter((item) => item !== "AgentPortal")
				.pop(),
		[pathname],
	);

	return selectedTab as string & keyof typeof tabs;
};

export const useCustomRouteMatch = () => {
	const match = useRouteMatch();
	const url = match.url === "/" ? "" : match.url;
	const path = match.path === "/" ? "" : match.path;

	return { url, path };
};
